/**
 * Utility functions for server-side SEO HTML transformation, meta tag injection,
 * and programmatic search console optimization.
 */

export const LEGACY_HTM_REDIRECTS: Record<string, string> = {
  '/sharedcontent.htm': '/',
  '/stockresearch.htm': '/',
  '/stockprice.htm': '/',
  '/disclaimer.htm': '/terms',
};

/**
 * Detects whether a request path contains template placeholders or malformed braces
 * such as {search_term_string} or URL-encoded variants.
 */
export function isMalformedSeoPath(reqPath: string): boolean {
  if (!reqPath) return false;
  const decoded = decodeURIComponent(reqPath).toLowerCase();
  return (
    decoded.includes('{') ||
    decoded.includes('}') ||
    decoded.includes('search_term_string') ||
    reqPath.includes('%7B') ||
    reqPath.includes('%7D') ||
    reqPath.includes('%7b') ||
    reqPath.includes('%7d')
  );
}

/**
 * Generates a clean canonical URL stripped of all search query parameters and fragments.
 */
export function generateCleanCanonical(baseUrl: string, pathname: string): string {
  const root = baseUrl.replace(/\/+$/, '');
  if (!pathname || pathname === '/' || pathname.trim() === '') {
    return `${root}/`;
  }
  const cleanPath = pathname.split('?')[0].split('#')[0];
  const normalized = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return `${root}${normalized}`;
}

/**
 * Builds a compliant XML sitemap for stock ticker analysis pages.
 */
export function generateStocksSitemap(
  baseUrl: string,
  tickers: string[],
  lastMod: string = new Date().toISOString().split('T')[0]
): string {
  const root = baseUrl.replace(/\/+$/, '');
  const urls = tickers
    .map(
      (ticker) => `  <url>
    <loc>${root}/analysis/${ticker.toUpperCase()}</loc>
    <lastmod>${lastMod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urls}
</urlset>`;
}

export function renderSeoHtml(rawHtml: string, reqPath: string): string {
  let canonicalUrl = 'https://stockinvestingpro.com/';
  let title = 'Stock Investing Pro | Algorithmic Stock Analysis & Portfolio Management';
  let ogTitle = 'Stock Investing Pro | Algorithmic Stock Diagnostics';
  let description =
    'Gain an unfair market advantage with institutional-grade algorithmic stock diagnostics, cycle-normalized DCF valuation models, and master confluence FA/TA rating indicators.';
  let tickerHeader = '';
  let tickerSummary = '';

  if (reqPath.startsWith('/analysis/')) {
    const rawTicker = reqPath.slice('/analysis/'.length).split('/')[0].split('?')[0];
    if (rawTicker && !rawTicker.includes('{') && !rawTicker.includes('}')) {
      const ticker = rawTicker.toUpperCase();
      canonicalUrl = `https://stockinvestingpro.com/analysis/${ticker}`;
      title = `${ticker} Stock Fair Value & DCF Valuation | Stock Investing Pro`;
      ogTitle = `${ticker} Fair Value & DCF | Stock Investing Pro`;
      description = `Institutional-grade financial diagnostics, cycle-normalized DCF fair value, and master confluence FA/TA rating indicators for ${ticker}.`;
      tickerHeader = `${ticker} Intrinsic Value, DCF Valuation &amp; Algorithmic Stock Diagnostics`;
      tickerSummary = `Institutional-grade financial diagnostics, cycle-normalized DCF fair value, statistical market regime weighting, and master confluence FA/TA rating indicators for ${ticker}.`;
    }
  } else if (reqPath !== '/' && reqPath !== '') {
    // For non-root static pages or clean paths
    const cleanPath = reqPath.startsWith('/') ? reqPath.slice(1) : reqPath;
    canonicalUrl = `https://stockinvestingpro.com/${cleanPath}`;
  }

  let html = rawHtml
    .replace(/<title>.*?<\/title>/i, `<title>${title}</title>`)
    .replace(/<meta name="description" content=".*?" \/>/i, `<meta name="description" content="${description}" />`)
    .replace(/<link rel="canonical" href=".*?" \/>/i, `<link rel="canonical" href="${canonicalUrl}" />`)
    .replace(/<meta property="og:title" content=".*?" \/>/i, `<meta property="og:title" content="${ogTitle}" />`)
    .replace(/<meta property="og:description" content=".*?" \/>/i, `<meta property="og:description" content="${description}" />`)
    .replace(/<meta property="og:url" content=".*?" \/>/i, `<meta property="og:url" content="${canonicalUrl}" />`)
    .replace(/<meta name="twitter:title" content=".*?" \/>/i, `<meta name="twitter:title" content="${ogTitle}" />`)
    .replace(/<meta name="twitter:description" content=".*?" \/>/i, `<meta name="twitter:description" content="${description}" />`);

  if (tickerHeader) {
    html = html.replace(/<h1([^>]*)>.*?<\/h1>/i, `<h1$1>${tickerHeader}</h1>`);
  }
  if (tickerSummary) {
    html = html.replace(/<p([^>]*)>Institutional-grade.*?<\/p>/i, `<p$1>${tickerSummary}</p>`);
  }

  return html;
}
